/**
 * Toast Component
 *
 * A reusable success/error/info toast notification component for MHR.
 * Renders as a fixed position notification in the bottom-right corner.
 */

import { useEffect } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info';

interface ToastProps {
  message: string | null;
  type?: ToastType;
  onClose: () => void;
  duration?: number;
}

export function Toast({ message, type = 'success', onClose, duration = 3000 }: ToastProps) {
  // Auto-dismiss after duration
  useEffect(() => {
    if (message && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [message, duration, onClose]);

  if (!message) return null;

  // Spec toast card: neutral surface + colored status icon (color never
  // carries meaning alone — the icon shape pairs with it).
  const config = {
    success: {
      icon: CheckCircle,
      iconColor: 'text-green-600 dark:text-green-400',
    },
    error: {
      icon: AlertCircle,
      iconColor: 'text-red-600 dark:text-red-400',
    },
    info: {
      icon: Info,
      iconColor: 'text-primary-600 dark:text-primary-400',
    },
  };

  const { icon: Icon, iconColor } = config[type];

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
      <div className="flex w-80 max-w-[calc(100vw-2rem)] items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800">
        <Icon size={20} className={`flex-shrink-0 ${iconColor}`} />
        <span className="min-w-0 flex-1 text-sm text-gray-900 dark:text-gray-100">{message}</span>
        <button
          onClick={onClose}
          className="-m-1 flex-shrink-0 rounded p-1 text-gray-400 transition-colors duration-150 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:text-gray-200"
          aria-label="Dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

/**
 * Inline Success Banner Component
 *
 * For pages that prefer an inline banner (like CriteriaEditor)
 * instead of a fixed-position toast.
 */
interface SuccessBannerProps {
  message: string | null;
  onClose?: () => void;
}

export function SuccessBanner({ message, onClose }: SuccessBannerProps) {
  if (!message) return null;

  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3 dark:bg-green-900/20 dark:border-green-800">
      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
      <p className="text-green-800 dark:text-green-300 flex-1 text-sm">{message}</p>
      {onClose && (
        <button
          onClick={onClose}
          className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
        >
          <X size={18} />
        </button>
      )}
    </div>
  );
}

export default Toast;
